import { ProfileProvider } from '@/providers/profile/profile.provider';
import { ALERTS_RIGHT_ACCESS } from '@/providers/rights.constants';
import { AdvizeoModalService } from '@/services/advizeo-modal.service';
import { Page, RoutingService } from '@/services/routing/routing.service';
import { UiChoiceModalComponent, type UiChoiceModalInput } from '@/ui/choice-modal/ui-choice-modal.component';
import { inject, Injectable } from '@angular/core';
import { type CanActivateChildFn, type CanActivateFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import type { Observable } from 'rxjs';
import { AlertScenarioType } from './alerts.interface';
import { DriftScenarioCreationModalComponent } from './drift/drift-scenario-creation-modal/drift-scenario-creation-modal.component';
import { ThresholdScenarioCreationModalComponent } from './threshold/threshold-scenario-creation-modal/threshold-scenario-creation-modal';
import { WaterLeakageScenarioCreationModalComponent } from './water-leakage/water-leakage-scenario-creation-modal/water-leakage-scenario-creation-modal';

@Injectable({
    providedIn: 'root'
})
export class AlertsFeatureService {
    private _router = inject(Router);
    private _profileProvider = inject(ProfileProvider);
    private _translate = inject(TranslateService);

    private _modalService = inject(AdvizeoModalService);
    private _routingService = inject(RoutingService);

    goToFeature() {
        this._router.navigate(['/', 'alerts']);
    }

    hasFeatureAccess$(): Observable<boolean> {
        return this._profileProvider.hasRight$(ALERTS_RIGHT_ACCESS);
    }

    hasFeatureAccess(): Promise<boolean> {
        return this._profileProvider.hasRight(ALERTS_RIGHT_ACCESS);
    }

    async requestAlertScenarioCreation() {
        const modal = this._modalService.openAdvizeoModal<UiChoiceModalInput<AlertScenarioType>, AlertScenarioType>(
            UiChoiceModalComponent,
            {
                title: this._translate.instant('PAGES.ALERT_SCENARIO.QUICK_ACTION_CREATE_SCENARIO.TITLE'),
                buttons: [
                    {
                        text: this._translate.instant('PAGES.ALERT_SCENARIO.QUICK_ACTION_CREATE_SCENARIO.THRESHOLD'),
                        code: AlertScenarioType.THRESHOLD as const
                    },
                    {
                        text: this._translate.instant('PAGES.ALERT_SCENARIO.QUICK_ACTION_CREATE_SCENARIO.WATER_LEAKAGE'),
                        code: AlertScenarioType.WATER_LEAKAGE as const
                    },
                    {
                        text: this._translate.instant('PAGES.ALERT_SCENARIO.QUICK_ACTION_CREATE_SCENARIO.DRIFT'),
                        code: AlertScenarioType.DRIFT as const
                    }
                ]
            }
        );

        const type = await modal.afterClosed();

        if (!type) {
            return;
        }

        switch (type) {
            case AlertScenarioType.THRESHOLD: {
                const alertScenarioId = await this._modalService
                    .openAdvizeoModal(ThresholdScenarioCreationModalComponent, null)
                    .afterClosed();
                if (alertScenarioId) {
                    await this._routingService.resetNavigationTo(Page.THRESHOLD_SCENARIO_LANDING, {});
                    this._routingService.goTo(Page.THRESHOLD_SCENARIO_EDITION, { alertScenarioId });
                }
                break;
            }

            case AlertScenarioType.WATER_LEAKAGE: {
                const alertScenarioId = await this._modalService
                    .openAdvizeoModal(WaterLeakageScenarioCreationModalComponent, null)
                    .afterClosed();
                if (alertScenarioId) {
                    await this._routingService.resetNavigationTo(Page.WATER_LEAKAGE_SCENARIO_LANDING, {});
                    this._routingService.goTo(Page.WATER_LEAKAGE_SCENARIO_EDITION, { alertScenarioId });
                }
                break;
            }

            case AlertScenarioType.DRIFT: {
                const alertScenarioId = await this._modalService.openAdvizeoModal(DriftScenarioCreationModalComponent, {}).afterClosed();
                if (alertScenarioId) {
                    await this._routingService.resetNavigationTo(Page.DRIFT_SCENARIO_LANDING, {});
                    this._routingService.goTo(Page.DRIFT_SCENARIO_EDITION, { alertScenarioId });
                }
                break;
            }
        }
    }

    canRequestAlertScenarioCreation$() {
        return this.hasFeatureAccess$();
    }
}

export const alertsGuard: CanActivateFn & CanActivateChildFn = async () => {
    const featureService = inject(AlertsFeatureService);
    const hasFeatureAccess = await featureService.hasFeatureAccess();

    if (!hasFeatureAccess) {
        // redirect ?
        return false;
    }
    return true;
};
